import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Card, Table, Container, Row, Col, Spinner, Badge, Alert } from 'react-bootstrap';
import { 
  ArrowLeft, 
  Plus, 
  Edit3, 
  Trash2, 
  Lock, 
  Calendar, 
  User, 
  CreditCard, 
  Calculator,
  AlertCircle,
  CheckCircle2,
  Clock,
  Building2,
  PlusCircle,
  List,
  GripVertical
} from 'lucide-react';
import axios from '../../../../services/AxiosInstance';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import SelectWageTypeModal from './SelectWageTypeModal';
import EditPayslipRowModal from './EditPayslipRowModal';
import { getAbsenceWageTypes } from './wageTypesConfig';
import { getApiUrl } from '../../../../utils/ApiUtils';
import Swal from 'sweetalert2';
import { toast } from 'sonner';

// Use comprehensive wage types configuration
const ABSENCE_WAGE_TYPES = getAbsenceWageTypes();

// Helper function to check if a wage type requires absence dates
const requiresAbsenceDates = (wageTypeCode) => {
  return ABSENCE_WAGE_TYPES.includes(wageTypeCode);
};

function Payslip({ match }) {
  const { companyId } = useParams();
  const history = useHistory();

  // State management
  const [payslip, setPayslip] = useState(null);
  const [payrollPeriod, setPayrollPeriod] = useState(null);
  const [wageTypes, setWageTypes] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);

  // UI state
  const [isPayslipLoading, setIsPayslipLoading] = useState(true);
  const [isAddingRow, setIsAddingRow] = useState(false);
  const [isDeletingRow, setIsDeletingRow] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Data fetching functions
  const fetchPayslipData = useCallback(async () => {
    setIsPayslipLoading(true);
    try {
      const url = getApiUrl(`${companyId}/payslip/${match.params.id}`);
      const response = await axios.get(url, { withCredentials: true });
      console.log('Payslip data:', response.data);
      
      // Backend now returns data directly in response.data (not wrapped in .data.data)
      const payslipData = response.data.data || response.data;
      setPayslip(payslipData);

      // Also fetch the payroll period to check lock status
      if (payslipData.payrollId) {
        const payrollUrl = getApiUrl(`${companyId}/payroll/${payslipData.payrollId}`);
        const payrollResponse = await axios.get(payrollUrl, { withCredentials: true });
        console.log('Payroll data:', payrollResponse.data);
        
        // Backend returns { status: "success", data: { ... } }
        setPayrollPeriod(payrollResponse.data.data || payrollResponse.data);
      }

    } catch (error) {
      console.error('Error fetching payslip:', error);
      // await Swal.fire({
      //   icon: 'error',
      //   title: 'Fel',
      //   text: 'Kunde inte hämta lönespecifikationen'
      // });
      toast.error('Kunde inte hämta lönespecifikationen');
    } finally {
      setIsPayslipLoading(false);
    }
  }, [companyId, match.params.id]);

  const fetchWageTypes = useCallback(async () => {
    try {
      const url = getApiUrl(`${companyId}/payroll/wage_types`);
      const response = await axios.get(url, { withCredentials: true });
      setWageTypes(response.data);
    } catch (error) {
      console.error('Error fetching wage types:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Fel',
        text: 'Kunde inte hämta lönetyper'
      });
    }
  }, [companyId]);

  // Initial data loading
  useEffect(() => {
    Promise.all([fetchPayslipData(), fetchWageTypes()]);
  }, [fetchPayslipData, fetchWageTypes]);

  // Helper functions
  const requiresAbsenceDates = (wageTypeCode) => {
    return ABSENCE_WAGE_TYPES.includes(wageTypeCode);
  };

  const isPayrollLocked = () => {
    return payrollPeriod?.isLocked || false;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('sv-SE');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Row operations handlers
  const handleAddRow = async (selectedWageTypes) => {
    if (isPayrollLocked()) {
      // Swal.fire({
      //   icon: 'warning',
      //   title: 'Period låst',
      //   text: 'Du kan inte lägga till rader när löneperioden är låst'
      // });
      toast.warning('Du kan inte lägga till rader när löneperioden är låst');
      return;
    }

    setIsAddingRow(true);
    try {
      const newRows = selectedWageTypes.map(wageType => {
        const row = {
          wage_type_id: wageType.wage_type_id || wageType.id, // Support both old and new format
          code: wageType.code,
          description: wageType.description,
          quantity: wageType.quantity || 1,
          inputType: wageType.inputType || 'hours'
        };

        // Add absence dates if they exist (for wage types that require them)
        if (wageType.absenceStartDate) {
          row.absenceStartDate = wageType.absenceStartDate;
        }
        if (wageType.absenceEndDate) {
          row.absenceEndDate = wageType.absenceEndDate;
        }

        return row;
      });

      console.log('Sending wage type rows:', newRows);

      const url = getApiUrl(`${companyId}/payslip/${match.params.id}/rows`);
      await axios.post(
        url,
        { rows: newRows, employeeId: payslip.employeeId },
        { withCredentials: true }
      );

      await fetchPayslipData();
      setShowAddModal(false);

      // Swal.fire({
      //   icon: 'success',
      //   title: 'Tillagt',
      //   text: `${newRows.length} rad(er) har lagts till`,
      //   timer: 1500,
      //   showConfirmButton: false
      // });
      toast.success(`${newRows.length} rad(er) har lagts till`);

    } catch (error) {
      // Show more detailed error message if available
      const errorMessage = error.response?.data?.details || 'Det gick inte att lägga till rader';
      
      // await Swal.fire({
      //   icon: 'error',
      //   title: 'Fel',
      //   text: errorMessage
      // });
      toast.error(errorMessage);
    } finally {
      setIsAddingRow(false);
    }
  };

  const handleUpdateRow = async (updatedRow) => {
    if (isPayrollLocked()) {
      // Swal.fire({
      //   icon: 'warning',
      //   title: 'Period låst',
      //   text: 'Du kan inte ändra rader när löneperioden är låst'
      // });
      toast.warning('Du kan inte ändra rader när löneperioden är låst');
      return;
    }

    try {
      const url = getApiUrl(`${companyId}/payslip/${match.params.id}/row/${updatedRow.rowId || updatedRow.id}`);
      await axios.put(url, {
        employeeId: payslip.employeeId,
        quantity: updatedRow.quantity,
        wageCode: updatedRow.wageType.code,
        inputType: updatedRow.inputType || 'hours',
        absenceStartDate: updatedRow.absenceStartDate || null,
        absenceEndDate: updatedRow.absenceEndDate || null,
        comment: updatedRow.comment || null
      }, { withCredentials: true });

      await fetchPayslipData();
      setShowEditModal(false);

      // Swal.fire({
      //   icon: 'success',
      //   title: 'Uppdaterat',
      //   text: 'Raden har uppdaterats',
      //   timer: 1500,
      //   showConfirmButton: false
      // });
      toast.success('Raden har uppdaterats');
    } catch (error) {
      // console.error('Error updating row:', error);
      // await Swal.fire({
      //   icon: 'error',
      //   title: 'Fel',
      //   text: 'Det gick inte att uppdatera raden'
      // });
      toast.error('Det gick inte att uppdatera raden');
    }
  };

  const handleDeleteRow = async () => {
    if (isPayrollLocked()) {
      // Swal.fire({
      //   icon: 'warning',
      //   title: 'Period låst',
      //   text: 'Du kan inte radera rader när löneperioden är låst'
      // });
      toast.warning('Du kan inte radera rader när löneperioden är låst');
      return;
    }

    try {
      const result = await Swal.fire({
        title: 'Är du säker?',
        text: "Denna åtgärd kan inte ångras!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ja, radera',
        cancelButtonText: 'Avbryt',
        confirmButtonColor: '#dc3545',
        reverseButtons: true
      });

      if (result.isConfirmed) {
        setIsDeletingRow(true);
        const url = getApiUrl(`${companyId}/payslip/${match.params.id}/row/${selectedItem.rowId || selectedItem.id}`);
        await axios.delete(url, { withCredentials: true });

        await fetchPayslipData();
        setSelectedItem(null);
        setShowEditModal(false);

        // Swal.fire({
        //   icon: 'success',
        //   title: 'Raderat',
        //   text: 'Raden har raderats',
        //   timer: 1500,
        //   showConfirmButton: false
        // });
        toast.success('Raden har raderats');
      }
    } catch (error) {
      console.error('Error deleting row:', error);
      // await Swal.fire({
      //   icon: 'error',
      //   title: 'Fel',
      //   text: 'Det gick inte att radera raden'
      // });
      toast.error('Det gick inte att radera raden');
    } finally {
      setIsDeletingRow(false);
    }
  };

  const handleBackToPayroll = () => {
    if (payrollPeriod) {
      history.push(`/${companyId}/payroll/${payrollPeriod.year}/${payrollPeriod.month}`);
    } else {
      history.goBack();
    }
  };

  // Handle drag and drop reordering (visual only - no backend persistence)
  const handleDragEnd = (result) => {
    if (!result.destination || isPayrollLocked()) {
      return;
    }

    const { source, destination } = result;
    
    if (source.index === destination.index) {
      return;
    }

    // Create new array with reordered items
    const newRows = Array.from(payslip.rows || []);
    const [reorderedItem] = newRows.splice(source.index, 1);
    newRows.splice(destination.index, 0, reorderedItem);

    // Update local state for visual reordering
    setPayslip(prev => ({
      ...prev,
      rows: newRows
    }));

    // Show info message that order is visual only
    Swal.fire({
      icon: 'info',
      title: 'Ordning ändrad',
      text: 'Radernas ordning har ändrats visuellt. Denna ordning sparas inte permanent.',
      timer: 2000,
      showConfirmButton: false,
      toast: true,
      position: 'top-end'
    });
  };

  if (isPayslipLoading || !payslip) {
    return (
      <div className="modern-loading-container">
        <Spinner animation="border" role="status" size="lg">
          <span className="visually-hidden">Laddar...</span>
        </Spinner>
        <p className="loading-text">Laddar lönespecifikation...</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .modern-payslip-container {
          background-color: #f8fafc;
          min-height: 100vh;
        }

        .modern-loading-container {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          min-height: 400px;
          gap: 1rem;
        }

        .loading-text {
          color: #64748b;
          font-size: 0.875rem;
          margin: 0;
        }

        .payslip-header {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          margin-bottom: 2rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
        }

        .header-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 2rem;
        }

        .back-button {
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          color: #64748b;
          border-radius: 6px;
          padding: 0.75rem 1rem;
          font-weight: 500;
          font-size: 0.875rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .back-button:hover {
          background: #e2e8f0;
          color: #475569;
          text-decoration: none;
        }

        .header-title {
          color: #1e293b;
          font-size: 1.5rem;
          font-weight: 600;
          margin: 0;
        }

        .header-subtitle {
          color: #64748b;
          font-size: 0.875rem;
          margin-top: 0.25rem;
          margin-bottom: 0;
        }

        .lock-status {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .lock-status.locked {
          background: #fef2f2;
          color: #dc2626;
          border: 1px solid #fecaca;
        }

        .lock-status.unlocked {
          background: #f0fdf4;
          color: #16a34a;
          border: 1px solid #bbf7d0;
        }

        .employee-info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 2rem;
          margin-bottom: 2rem;
        }

        .info-card {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 1.5rem;
        }

        .info-card-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .info-card-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          margin: 0;
        }

        .info-card-content {
          space-y: 0.5rem;
        }

        .info-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        .info-label {
          color: #64748b;
          font-size: 0.875rem;
        }

        .info-value {
          font-weight: 500;
          color: #1e293b;
          font-size: 0.875rem;
        }

        .payslip-rows-section {
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
          margin-bottom: 2rem;
          overflow: hidden;
        }

        .payslip-section-header {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          padding: 1.5rem 2rem;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .section-title {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 1.125rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .add-row-btn {
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 0.75rem 1.5rem;
          font-weight: 500;
          font-size: 0.875rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .add-row-btn:hover:not(:disabled) {
          background: #2563eb;
          transform: translateY(-1px);
        }

        .add-row-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .lock-notice {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
          padding: 1rem 2rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .payslip-rows-container {
          padding: 1rem 2rem;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .payslip-row {
          background: #f8fafc;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 1rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          transition: all 0.2s ease;
          position: relative;
          min-height: 60px;
        }

        .payslip-row.editable {
          cursor: pointer;
        }

        .payslip-row.editable:hover {
          background: #f1f5f9;
          border-color: #cbd5e1;
          transform: translateY(-1px);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .payslip-row.locked {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .row-drag-handle {
          color: #9ca3af;
          cursor: grab;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0.5rem;
          border-radius: 6px;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }

        .row-drag-handle:hover {
          background: #e5e7eb;
          color: #6b7280;
        }

        .row-drag-handle:active {
          cursor: grabbing;
        }

        .payslip-row:hover .row-drag-handle {
          color: #6b7280;
        }

        .row-content {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 2rem;
        }

        .payslip-wage-type-info {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          min-width: 200px;
          flex-shrink: 0;
          justify-content: center;
        }

        .payslip-wage-type-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .wage-code {
          font-size: 0.875rem;
          font-weight: 600;
          color: #3b82f6;
        }

        .wage-description {
          font-size: 0.875rem;
          font-weight: 500;
          color: #1e293b;
        }

        .wage-comment {
          color: #6b7280;
          font-style: italic;
          font-size: 0.75rem;
        }

        .row-details {
          display: flex;
          align-items: center;
          gap: 2rem;
          justify-content: center;
        }

        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 0.125rem;
          text-align: center;
          min-width: 80px;
          justify-content: center;
        }

        .detail-item.total {
          min-width: 100px;
        }

        .detail-label {
          font-size: 0.75rem;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .detail-value {
          font-size: 0.875rem;
          font-weight: 500;
          color: #1e293b;
        }

        .detail-value.amount {
          font-size: 1rem;
          font-weight: 700;
        }

        .absence-info {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          color: #16a34a;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .absence-warning {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          color: #f59e0b;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .empty-state {
          padding: 4rem 2rem;
          text-align: center;
          color: #6b7280;
        }

        .empty-state h3 {
          color: #374151;
          font-size: 1.25rem;
          font-weight: 600;
          margin: 1rem 0 0.5rem 0;
        }

        .empty-state p {
          font-size: 0.875rem;
          margin-bottom: 2rem;
        }

        .add-first-row-btn {
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 1rem 2rem;
          font-weight: 500;
          font-size: 1rem;
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .add-first-row-btn:hover:not(:disabled) {
          background: #2563eb;
          transform: translateY(-1px);
        }

        .add-first-row-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .modern-btn {
          border-radius: 6px;
          padding: 0.75rem 1.5rem;
          font-weight: 500;
          font-size: 0.875rem;
          border: none;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
        }

        .btn-modern-primary {
          background: var(--bs-primary);
          color: white;
        }

        .btn-modern-primary:hover:not(:disabled) {
          background: var(--bs-primary) ;
          transform: translateY(-1px);
        }

        .btn-modern-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .summary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
        }

        .summary-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 1.5rem;
        }

        .summary-card-title {
          font-size: 1rem;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .summary-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 0;
          border-bottom: 1px solid #f3f4f6;
        }

        .summary-item:last-child {
          border-bottom: none;
          font-weight: 600;
          font-size: 1.125rem;
          color: #059669;
        }

        .summary-label {
          color: #64748b;
          font-size: 0.875rem;
        }

        .summary-value {
          font-weight: 500;
          color: #1e293b;
        }

        .warning-alert {
          background: #fffbeb;
          border: 1px solid #fed7aa;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 2rem;
          display: flex;
          gap: 0.75rem;
        }

        .warning-icon {
          color: #ea580c;
          flex-shrink: 0;
          margin-top: 0.125rem;
        }

        .warning-text {
          color: #9a3412;
          font-size: 0.875rem;
          line-height: 1.5;
        }

        @media (max-width: 768px) {
          .modern-payslip-container {
            padding: 1rem;
          }

          .payslip-header {
            padding: 1.5rem;
          }

          .header-top {
            flex-direction: column;
            gap: 1rem;
            align-items: stretch;
          }

          .employee-info-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
          }

          .section-header {
            flex-direction: column;
            gap: 1rem;
            align-items: stretch;
          }

          .add-row-btn {
            width: 100%;
            justify-content: center;
          }

          .payslip-rows-container {
            padding: 0.75rem;
          }

          .payslip-row {
            flex-direction: column;
            gap: 0.75rem;
            padding: 0.75rem;
          }

          .row-content {
            flex-direction: column;
            align-items: stretch;
            gap: 0.75rem;
          }

          .row-drag-handle {
            align-self: flex-start;
          }

          .row-details {
            flex-direction: row;
            justify-content: space-between;
            gap: 1rem;
          }

          .detail-item {
            text-align: center;
            min-width: auto;
            flex: 1;
          }

          .wage-type-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }

          .wage-type-info {
            min-width: auto;
          }

          .summary-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
          }
        }
      `}</style>

      <div className="modern-payslip-container">
        {/* Header */}
        <div className="payslip-header">
          <div className="header-top">
            <div>
              <button className="back-button" onClick={handleBackToPayroll}>
                <ArrowLeft size={16} />
                Tillbaka till lönekörning
              </button>
            </div>
            <div className="text-end">
              <div className={`lock-status ${isPayrollLocked() ? 'locked' : 'unlocked'}`}>
                {isPayrollLocked() ? (
                  <>
                    <Lock size={16} />
                    Period låst
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={16} />
                    Redigerbar
                  </>
                )}
              </div>
            </div>
          </div>

          <h1 className="header-title">Lönespecifikation</h1>
          <p className="header-subtitle">
            {payrollPeriod ? 
              `${payrollPeriod.year} ${new Date(payrollPeriod.year, payrollPeriod.month - 1).toLocaleDateString('sv-SE', { month: 'long' })}` : 
              'Aktuell period'
            }
          </p>
        </div>

        {/* Lock Warning */}
        {isPayrollLocked() && (
          <div className="warning-alert">
            <AlertCircle size={20} className="warning-icon" />
            <div className="warning-text">
              <strong>Löneperioden är låst</strong><br />
              Du kan visa lönespecifikationen men inte göra ändringar. Lås upp perioden i lönekörningen för att redigera.
            </div>
          </div>
        )}

        {/* Employee Information */}
        <div className="employee-info-grid">
          <div className="info-card">
            <div className="info-card-header">
              <User size={18} color="#3b82f6" />
              <h3 className="info-card-title">Anställd</h3>
            </div>
            <div className="info-card-content">
              <div className="info-item">
                <span className="info-label">Namn:</span>
                <span className="info-value">{payslip.employee?.fullName || `${payslip.employee?.firstname || ''} ${payslip.employee?.lastname || ''}`}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Personnummer:</span>
                <span className="info-value">{payslip.employee?.ssn}</span>
              </div>
            </div>
          </div>

          <div className="info-card">
            <div className="info-card-header">
              <Calculator size={18} color="#3b82f6" />
              <h3 className="info-card-title">Skatteinformation</h3>
            </div>
            <div className="info-card-content">
              <div className="info-item">
                <span className="info-label">Skattetabell:</span>
                <span className="info-value">{payslip.contractTaxTable}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Skattekolumn:</span>
                <span className="info-value">{payslip.contractTaxColumn}</span>
              </div>
            </div>
          </div>

          <div className="info-card">
            <div className="info-card-header">
              <Building2 size={18} color="#3b82f6" />
              <h3 className="info-card-title">Företag</h3>
            </div>
            <div className="info-card-content">
              <div className="info-item">
                <span className="info-label">Period start:</span>
                <span className="info-value">{formatDate(payslip.periodStartDate)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Period slut:</span>
                <span className="info-value">{formatDate(payslip.periodEndDate)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Payslip Rows */}
        <div className="payslip-rows-section">
          <div className="payslip-section-header">
            <h2 className="section-title">
              <List size={20} />
              Lönerader
            </h2>
            <button
              onClick={() => setShowAddModal(true)}
              className="add-row-btn"
              disabled={isAddingRow || isPayrollLocked()}
            >
              {isAddingRow ? (
                <>
                  <Spinner size="sm" />
                  Lägger till...
                </>
              ) : (
                <>
                  <PlusCircle size={16} />
                  Lägg till lönerad
                </>
              )}
            </button>
          </div>

          {isPayrollLocked() && (
            <div className="lock-notice">
              <Lock size={16} />
              Lås upp löneperioden för att redigera lönerader
            </div>
          )}

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="salaryItems">
              {(provided) => (
                <div className="payslip-rows-container" ref={provided.innerRef} {...provided.droppableProps}>
                  {(payslip.rows || []).map((item, index) => (
                    <Draggable key={item.rowId || item.id} draggableId={item.rowId || item.id} index={index}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`payslip-row ${isPayrollLocked() ? 'locked' : 'editable'}`}
                          onClick={() => {
                            if (!isPayrollLocked()) {
                              setSelectedItem(item);
                              setShowEditModal(true);
                            }
                          }}
                        >
                          <div className="row-drag-handle">
                            <GripVertical size={16} />
                          </div>
                          
                          <div className="row-content">
                            <div className="payslip-wage-type-info">
                              <div className="payslip-wage-type-header">
                                <span className="wage-code">{item.wageType.code}</span>
                                <span className="wage-description">{item.wageType.description}</span>
                              </div>
                              {item.comment && (
                                <div className="wage-comment">{item.comment}</div>
                              )}
                            </div>

                            {item.absenceStartDate && item.absenceEndDate && (
                              <div className="absence-info">
                                <Calendar size={14} />
                                <span>
                                  {formatDate(item.absenceStartDate)} - {formatDate(item.absenceEndDate)}
                                </span>
                              </div>
                            )}

                            {requiresAbsenceDates(item.wageType.code) && !item.absenceStartDate && (
                              <div className="absence-warning">
                                <AlertCircle size={14} />
                                <span>Frånvarodatum saknas</span>
                              </div>
                            )}
                            
                            <div className="row-details">
                              <div className="detail-item">
                                <span className="detail-label">Antal</span>
                                <span className="detail-value">
                                  {item.quantity} {item.wageType.unit}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="detail-label">Kostnad</span>
                                <span className="detail-value">{formatCurrency(item.rate)}</span>
                              </div>
                              <div className="detail-item total">
                                <span className="detail-label">Summa</span>
                                <span 
                                  className="detail-value amount"
                                  style={{ 
                                    color: item.wageType.effectType === 'dec' ? '#dc2626' : '#059669'
                                  }}
                                >
                                  {item.wageType.effectType === 'dec' ? '-' : ''}
                                  {formatCurrency(Math.abs(item.amount))}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          {(!payslip.rows || payslip.rows.length === 0) && (
            <div className="empty-state">
              <Edit3 size={48} color="#9ca3af" />
              <h3>Inga lönerader än</h3>
              <p>Lägg till lönerader för att börja bygga lönespecifikationen</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="add-first-row-btn"
                disabled={isPayrollLocked()}
              >
                <PlusCircle size={20} />
                Lägg till första löneraden
              </button>
            </div>
          )}
        </div>

        {/* Summary Section */}
        <div className="summary-grid">
          <div className="summary-card">
            <h3 className="summary-card-title">
              <CreditCard size={18} />
              Lönesammanfattning
            </h3>
            <div className="summary-item">
              <span className="summary-label">Bruttolön</span>
              <span className="summary-value">{formatCurrency(payslip.grossPay)}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Skatt</span>
              <span className="summary-value">{formatCurrency(payslip.tax)}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Nettolön</span>
              <span className="summary-value">{formatCurrency(payslip.netPay)}</span>
            </div>
          </div>

          <div className="summary-card">
            <h3 className="summary-card-title">
              <Building2 size={18} />
              Arbetsgivaravgifter
            </h3>
            <div className="summary-item">
              <span className="summary-label">Sociala avgifter</span>
              <span className="summary-value">{formatCurrency(payslip.socialSecContr)}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Total lönekostnad</span>
              <span className="summary-value">
                {formatCurrency(Number(payslip.grossPay) + Number(payslip.socialSecContr))}
              </span>
            </div>
          </div>

          <div className="summary-card">
            <h3 className="summary-card-title">
              <Clock size={18} />
              Betalningsinformation
            </h3>
            <div className="summary-item">
              <span className="summary-label">Clearingnummer</span>
              <span className="summary-value">{payslip.bankAccount?.clearingNumber || 'Ej angivet'}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Kontonummer</span>
              <span className="summary-value">{payslip.bankAccount?.accountNumber || 'Ej angivet'}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Utbetalningsdatum</span>
              <span className="summary-value">{payrollPeriod?.payoutDate ? formatDate(payrollPeriod.payoutDate) : 'Ej satt'}</span>
            </div>
          </div>
        </div>

        {/* Modals */}
        <SelectWageTypeModal
          show={showAddModal}
          handleClose={() => setShowAddModal(false)}
          handleSelect={handleAddRow}
          wageTypes={wageTypes}
        />
        <EditPayslipRowModal
          show={showEditModal}
          handleClose={() => setShowEditModal(false)}
          item={selectedItem}
          contract={payslip?.contract}
          handleEdit={handleUpdateRow}
          handleDelete={handleDeleteRow}
          isLocked={isPayrollLocked()}
        />
      </div>
    </>
  );
}

export default Payslip;



// import React, { useState, useEffect, useCallback } from 'react';
// import { useParams } from 'react-router-dom';
// import { Card, Table, Container, Row, Col, Spinner, Badge } from 'react-bootstrap';
// import axios from '../../../../services/AxiosInstance';
// import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
// import SelectWageTypeModal from './SelectWageTypeModal';
// import EditPayslipRowModal from './EditPayslipRowModal';
// import { getApiUrl } from '../../../../utils/ApiUtils';
// import Swal from 'sweetalert2';

// // Define wage types that require absence dates (like VAB)
// const ABSENCE_WAGE_TYPES = ['3301', '3305', '3318']; // Update with your actual codes

// function Payslip({ match }) {
//   // Styles remain the same
//   useEffect(() => {
//     const styleSheet = document.createElement('style');
//     styleSheet.textContent = `
//     /* Main container styles */
//     .payslip-wrapper {
//       padding: 20px;
//       background-color: #ffffff;
//     }

//     /* Header section styles */
//     .header-section {
//       margin-bottom: 2rem;
//     }
    
//     .header-title {
//       color: #6c757d;
//       font-size: 1.1rem;
//       font-weight: 500;
//     }

//     /* Information box styles */
//     .info-box {
//       border-top: 1px solid #dee2e6;
//       padding-top: 1rem;
//       margin-bottom: 1.5rem;
//     }

//     .info-label {
//       color: #6c757d;
//       font-size: 0.875rem;
//       display: block;
//       margin-bottom: 0.5rem;
//     }

//     .info-value {
//       font-weight: 300;
//       font-size: 1.1rem;
//       margin-bottom: 0.25rem;
//     }

//     /* Table styles */
//     .payslip-table {
//       margin-top: 1rem;
//     }

//     .payslip-table th {
//       background-color: #f8f9fa;
//       font-weight: 500;
//       padding: 0.75rem;
//       border-bottom: 2px solid #dee2e6;
//     }

//     .payslip-table td {
//       padding: 0.75rem;
//       vertical-align: middle;
//     }

//     .payslip-table tbody tr {
//       cursor: pointer;
//       transition: background-color 0.15s ease-in-out;
//     }

//     .payslip-table tbody tr:hover {
//       background-color: rgba(0, 0, 0, 0.02);
//     }

//     /* Summary section styles */
//     .summary-table td {
//       padding: 1rem;
//       border-top: 1px solid #dee2e6;
//     }

//     .summary-label {
//       text-transform: uppercase;
//       font-size: 0.875rem;
//       color: #495057;
//     }

//     .summary-value {
//       text-align: right;
//       font-size: 1.25rem;
//       font-weight: 500;
//     }

//     /* Stats section styles */
//     .stats-section {
//       margin-top: 2rem;
//     }

//     .stats-box {
//       border-top: 1px solid #dee2e6;
//       padding-top: 1rem;
//     }

//     .stats-title {
//       font-size: 1rem;
//       font-weight: 500;
//       margin-bottom: 1rem;
//     }

//     .stats-row {
//       display: flex;
//       justify-content: space-between;
//       margin-bottom: 0.5rem;
//     }

//     /* Button styles */
//     .add-button {
//       min-width: 150px;
//       margin-top: 1rem;
//     }

//     /* Loading spinner styles */
//     .loading-container {
//       display: flex;
//       justify-content: center;
//       align-items: center;
//       min-height: 200px;
//     }

//     /* Drag handle styles */
//     .drag-handle {
//       color: #6c757d;
//       cursor: grab;
//     }

//     .drag-handle:active {
//       cursor: grabbing;
//     }
    
//     /* New styles for absence dates */
//     .absence-badge {
//       font-size: 0.75rem;
//       margin-left: 0.5rem;
//     }
    
//     .comment-text {
//       color: #6c757d;
//       font-style: italic;
//       font-size: 0.85rem;
//       margin-top: 0.25rem;
//       display: block;
//     }
//     `;
//     document.head.appendChild(styleSheet);

//     // Cleanup on unmount
//     return () => {
//       document.head.removeChild(styleSheet);
//     };
//   }, []);


//   const { companyId } = useParams();

//   // State management
//   const [payslip, setPayslip] = useState(null);
//   const [wageTypes, setWageTypes] = useState([]);
//   const [selectedItem, setSelectedItem] = useState(null);

//   // UI state
//   const [isPayslipLoading, setIsPayslipLoading] = useState(true);
//   const [isAddingRow, setIsAddingRow] = useState(false);
//   const [isDeletingRow, setIsDeletingRow] = useState(false);
//   const [showAddModal, setShowAddModal] = useState(false);
//   const [showEditModal, setShowEditModal] = useState(false);

//   // Data fetching functions
//   const fetchPayslipData = useCallback(async () => {
//     setIsPayslipLoading(true);
//     try {
//       const url = getApiUrl(`${companyId}/payslip/${match.params.id}`);
//       const response = await axios.get(url, { withCredentials: true });
//       console.log('Payslip data:', response.data);
//       setPayslip(response.data);
//     } catch (error) {
//       console.error('Error fetching payslip:', error);
//       await Swal.fire({
//         icon: 'error',
//         title: 'Fel',
//         text: 'Kunde inte hämta lönespecifikationen'
//       });
//     } finally {
//       setIsPayslipLoading(false);
//     }
//   }, [companyId, match.params.id]);

//   const fetchWageTypes = useCallback(async () => {
//     try {
//       const url = getApiUrl(`${companyId}/payroll/wage_types`);
//       const response = await axios.get(url, { withCredentials: true });
//       setWageTypes(response.data);
//     } catch (error) {
//       console.error('Error fetching wage types:', error);
//       await Swal.fire({
//         icon: 'error',
//         title: 'Fel',
//         text: 'Kunde inte hämta lönetyper'
//       });
//     }
//   }, [companyId]);

//   // Initial data loading
//   useEffect(() => {
//     Promise.all([fetchPayslipData(), fetchWageTypes()]);
//   }, [fetchPayslipData, fetchWageTypes]);

//   // Helper to check if a wage type requires absence dates
//   const requiresAbsenceDates = (wageTypeCode) => {
//     return ABSENCE_WAGE_TYPES.includes(wageTypeCode);
//   };

//   // Row operations handlers
//   const handleAddRow = async (selectedWageTypes) => {
//     setIsAddingRow(true);
//     try {
//       const newRows = selectedWageTypes.map(wageType => ({
//         wage_type_id: wageType.wage_type_id,
//         code: wageType.code,
//         description: wageType.description,
//       }));

//       const url = getApiUrl(`${companyId}/payslip/${match.params.id}/rows`);
//       await axios.post(
//         url,
//         { rows: newRows, employeeId: payslip.values.employeeId },
//         { withCredentials: true }
//       );

//       await fetchPayslipData();
//       setShowAddModal(false);

//     } catch (error) {
//       console.error('Error adding rows:', error);
//       await Swal.fire({
//         icon: 'error',
//         title: 'Fel',
//         text: 'Det gick inte att lägga till rader'
//       });
//     } finally {
//       setIsAddingRow(false);
//     }
//   };

//   const handleUpdateRow = async (updatedRow) => {
//     try {
//       const url = getApiUrl(`${companyId}/payslip/${match.params.id}/row/${updatedRow.id}`);
//       await axios.put(url, {
//         employeeId: payslip.values.employeeId,
//         quantity: updatedRow.quantity,
//         wageCode: updatedRow.wageType.code,
//         // Add new fields for absence dates and comments
//         absenceStartDate: updatedRow.absenceStartDate || null,
//         absenceEndDate: updatedRow.absenceEndDate || null,
//         comment: updatedRow.comment || null
//       }, { withCredentials: true });

//       await fetchPayslipData();
//       setShowEditModal(false);
//     } catch (error) {
//       console.error('Error updating row:', error);
//       await Swal.fire({
//         icon: 'error',
//         title: 'Fel',
//         text: 'Det gick inte att uppdatera raden'
//       });
//     }
//   };

//   const handleDeleteRow = async () => {
//     try {
//       const result = await Swal.fire({
//         title: 'Är du säker?',
//         text: "Denna åtgärd kan inte ångras!",
//         icon: 'warning',
//         showCancelButton: true,
//         confirmButtonText: 'Ja, radera',
//         cancelButtonText: 'Avbryt',
//         confirmButtonColor: '#dc3545',
//         reverseButtons: true
//       });

//       if (result.isConfirmed) {
//         setIsDeletingRow(true);
//         const url = getApiUrl(`${companyId}/payslip/${match.params.id}/row/${selectedItem.id}`);
//         await axios.delete(url, { withCredentials: true });

//         await fetchPayslipData();
//         setSelectedItem(null);
//         setShowEditModal(false);
//       }
//     } catch (error) {
//       console.error('Error deleting row:', error);
//       await Swal.fire({
//         icon: 'error',
//         title: 'Fel',
//         text: 'Det gick inte att radera raden'
//       });
//     } finally {
//       setIsDeletingRow(false);
//     }
//   };

//   // Format date for display
//   const formatDate = (dateString) => {
//     if (!dateString) return '';
//     const date = new Date(dateString);
//     return date.toLocaleDateString('sv-SE'); // Format as YYYY-MM-DD
//   };

//   if (isPayslipLoading || !payslip) {
//     return (
//       <div className="loading-container">
//         <Spinner animation="border" role="status">
//           <span className="visually-hidden">Laddar...</span>
//         </Spinner>
//       </div>
//     );
//   }

//   return (
//     <div className="payslip-wrapper">
//       {/* Header Section */}
//       <div className="header-section">
//         <Row className="align-items-center">
//           <Col>
//             <h6 className="header-title">
//               Lönespecifikation för perioden {new Date().getFullYear()} {new Date().toLocaleString('sv-SE', { month: 'long' })}
//             </h6>
//           </Col>
//           <Col className="text-end">
//             <h6 className="header-title">{payslip.companyName}</h6>
//           </Col>
//         </Row>
//       </div>

//       {/* Employee Information */}
//       <Row className="mb-4">
//         <Col>
//           <div className="info-box">
//             <span className="info-label">Betalas till</span>
//             <h6 className="info-value">{payslip.values.name}</h6>
//             <h6 className="info-value">{payslip.values.ssn}</h6>
//           </div>
//         </Col>
//         <Col>
//           <div className="info-box">
//             <span className="info-label">Skatt</span>
//             <h6 className="info-value">Skattetabell: {payslip.values.taxTable}</h6>
//             <h6 className="info-value">Skattekolumn: {payslip.values.taxColumn}</h6>
//           </div>
//         </Col>
//       </Row>

//       {/* Payslip Rows */}
//       <Card>
//         <Card.Body>
//           <DragDropContext onDragEnd={() => { }}>
//             <Droppable droppableId="salaryItems">
//               {(provided) => (
//                 <Table className="payslip-table" responsive ref={provided.innerRef} {...provided.droppableProps}>
//                   <thead>
//                     <tr>
//                       <th></th>
//                       <th>Benämning</th>
//                       <th>Antal</th>
//                       <th>Kostnad</th>
//                       <th>Summa</th>
//                       <th>Frånvaroperiod</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {payslip.paySlipRows.map((item, index) => (
//                       <Draggable key={item.id} draggableId={item.id} index={index}>
//                         {(provided) => (
//                           <tr
//                             ref={provided.innerRef}
//                             {...provided.draggableProps}
//                             {...provided.dragHandleProps}
//                             onClick={() => {
//                               setSelectedItem(item);
//                               setShowEditModal(true);
//                             }}
//                           >
//                             <td>
//                               <i className="bi bi-list-ul drag-handle"></i>
//                             </td>
//                             <td>
//                               {item.wageType.code}, {item.wageType.description}
//                               {item.comment && (
//                                 <span className="comment-text">{item.comment}</span>
//                               )}
//                             </td>
//                             <td>{item.quantity} {item.wageType.wageTypeUnit}</td>
//                             <td>{item.rate}</td>
//                             <td>
//                               {item.wageType.wageTypeEffectType === 'dec' ? -item.amount : item.amount}
//                             </td>
//                             <td>
//                               {item.absenceStartDate && item.absenceEndDate ? (
//                                 `${formatDate(item.absenceStartDate)} - ${formatDate(item.absenceEndDate)}`
//                               ) : (
//                                 requiresAbsenceDates(item.wageType.code) ? (
//                                   <Badge bg="warning" pill>Saknas</Badge>
//                                 ) : null
//                               )}
//                             </td>
//                           </tr>
//                         )}
//                       </Draggable>
//                     ))}
//                     {provided.placeholder}
//                   </tbody>
//                 </Table>
//               )}
//             </Droppable>
//           </DragDropContext>

//           <button
//             onClick={() => setShowAddModal(true)}
//             className="btn btn-primary add-button"
//             disabled={isAddingRow}
//           >
//             {isAddingRow ? (
//               <>
//                 <Spinner size="sm" className="me-2" />
//                 Lägger till...
//               </>
//             ) : (
//               'Lägg till ny rad'
//             )}
//           </button>
//         </Card.Body>
//       </Card>

//       {/* Summary Section */}
//       <Card className="mt-4">
//         <Card.Body>
//           <Table className="summary-table">
//             <tbody>
//               <tr>
//                 <td className="summary-label">Total bruttolön för perioden</td>
//                 <td className="summary-value">{payslip.values.grossIncome} kr</td>
//               </tr>
//               <tr>
//                 <td className="summary-label">Din skatt enligt tabell</td>
//                 <td className="summary-value">{payslip.values.taxTotal} kr</td>
//               </tr>
//               <tr>
//                 <td className="summary-label">Sociala avgifter</td>
//                 <td className="summary-value">{payslip.values.socialTaxTotal} kr</td>
//               </tr>
//               <tr>
//                 <td className="summary-label">Total lönekostnad för företaget</td>
//                 <td className="summary-value">
//                   {(Number(payslip.values.grossIncome) + Number(payslip.values.socialTaxTotal)).toFixed(2)} kr
//                 </td>
//               </tr>
//               <tr>
//                 <td className="summary-label">Nettolön</td>
//                 <td className="summary-value">{payslip.values.netTotal} kr</td>
//               </tr>
//             </tbody>
//           </Table>
//         </Card.Body>
//       </Card>

//       {/* Statistics Section */}
//       <div className="stats-section">
//         <Row>
//           <Col md={6}>
//             <div className="stats-box">
//               <h6 className="stats-title">Semesterdagar</h6>
//               <div className="stats-row">
//                 <span>Betalda:</span>
//                 <span>{payslip.vacationDays.allowed}</span>
//               </div>
//               <div className="stats-row">
//                 <span>Sparade:</span>
//                 <span>{payslip.vacationDays.allowed - payslip.vacationDays.taken}</span>
//               </div>
//             </div>
//           </Col>
//           <Col md={6}>
//             <div className="stats-box">
//               <h6 className="stats-title">Totalt detta år</h6>
//               <div className="stats-row">
//                 <span>Brutto:</span>
//                 <span>{payslip.yearlyTotals.salaryTotal} kr</span>
//               </div>
//               <div className="stats-row">
//                 <span>Skatt:</span>
//                 <span>{payslip.yearlyTotals.taxTotal} kr</span>
//               </div>
//               <div className="stats-row">
//                 <span>Förmåner:</span>
//                 <span>{payslip.yearlyTotals.benefits} kr</span>
//               </div>
//             </div>
//           </Col>
//         </Row>
//       </div>

//       {/* Modals */}
//       <SelectWageTypeModal
//         show={showAddModal}
//         handleClose={() => setShowAddModal(false)}
//         handleSelect={handleAddRow}
//         wageTypes={wageTypes}
//       />
//       <EditPayslipRowModal
//         show={showEditModal}
//         handleClose={() => setShowEditModal(false)}
//         item={selectedItem}
//         handleEdit={handleUpdateRow}
//         handleDelete={handleDeleteRow}
//       />
//     </div>
//   );
// }

// export default Payslip;